/**
 * Home page - bigflowdata company overview.
 *
 * Structure:
 * 1. Hero — Provocative headline, CTA to /pairs
 * 2. Problem — Real merchant pain points
 * 3. Product Spotlight — bigflow.pairs with demo
 * 4. Results — 3 concrete impact metrics
 * 5. How It Works — 3 steps (Connect, Analyze, Grow)
 * 6. Trust Signals — Security, GDPR, no lock-in
 * 7. Final CTA — Link to /pairs
 */

import { Link } from 'react-router-dom'
import { useEffect } from 'react'
import {
  ArrowRight,
  Sparkles,
  BarChart3,
  Zap,
  Shield,
  TrendingUp,
  Users,
  Clock,
  HelpCircle,
  ShoppingCart,
  Target,
  Lock,
} from 'lucide-react'

export function Home() {
  useEffect(() => {
    document.title = 'bigflowdata — Data-Driven Tools for Shopify Merchants'
  }, [])

  return (
    <div className="pt-16">
      {/* ============================================================
          HERO — Provocative headline
          ============================================================ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-50" />
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-500/10 rounded-full blur-[128px]" />
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-brand-500/5 rounded-full blur-[96px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-24 lg:py-36">
          <div className="text-center max-w-4xl mx-auto">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-500/10 text-brand-500 text-sm font-medium mb-8 animate-fade-in"
            >
              <Sparkles className="h-4 w-4" />
              Intelligence Tools for Shopify
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-7xl font-black mb-6 animate-fade-in leading-tight"
              style={{ animationDelay: '0.1s' }}
            >
              Your Shopify data is
              <span className="gradient-text block"> telling you something</span>
            </h1>

            <p
              className="text-lg lg:text-xl text-[var(--color-text-secondary)] mb-10 max-w-2xl mx-auto animate-fade-in"
              style={{ animationDelay: '0.2s' }}
            >
              Every order your customers place holds a pattern. We build tools
              that reveal those patterns and turn them into revenue.
            </p>

            <div
              className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in"
              style={{ animationDelay: '0.3s' }}
            >
              <Link
                to="/pairs"
                className="inline-flex items-center gap-2 px-8 py-4 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-2xl transition-all hover:scale-105"
              >
                See What Your Data Reveals
                <ArrowRight className="h-5 w-5" />
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[var(--color-border)] hover:bg-[var(--color-text-muted)]/20 font-semibold rounded-2xl transition-all"
              >
                How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          THE PROBLEM — Merchant pain
          ============================================================ */}
      <section className="py-20 lg:py-28 section-alt">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              You have the data. You just don't have the tools.
            </h2>
            <p className="text-[var(--color-text-secondary)] text-lg max-w-3xl mx-auto">
              Most Shopify merchants sit on thousands of orders without knowing
              what those orders are trying to tell them.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card-gradient rounded-2xl p-8 border border-[var(--color-border)]">
              <div className="w-12 h-12 rounded-xl bg-brand-500/10 flex items-center justify-center mb-5">
                <HelpCircle className="h-6 w-6 text-brand-500" />
              </div>
              <h3 className="text-lg font-bold mb-3">Guessing what to promote</h3>
              <p className="text-[var(--color-text-muted)] text-sm leading-relaxed">
                Which products should you bundle? What should you cross-sell on
                each product page? Without data, every decision is a guess.
              </p>
            </div>

            <div className="card-gradient rounded-2xl p-8 border border-[var(--color-border)]">
              <div className="w-12 h-12 rounded-xl bg-brand-500/10 flex items-center justify-center mb-5">
                <Clock className="h-6 w-6 text-brand-500" />
              </div>
              <h3 className="text-lg font-bold mb-3">Hours of manual work</h3>
              <p className="text-[var(--color-text-muted)] text-sm leading-relaxed">
                Exporting CSVs, building spreadsheets, trying to spot patterns
                by hand. Time you should be spending growing your business.
              </p>
            </div>

            <div className="card-gradient rounded-2xl p-8 border border-[var(--color-border)]">
              <div className="w-12 h-12 rounded-xl bg-brand-500/10 flex items-center justify-center mb-5">
                <ShoppingCart className="h-6 w-6 text-brand-500" />
              </div>
              <h3 className="text-lg font-bold mb-3">Revenue left on the table</h3>
              <p className="text-[var(--color-text-muted)] text-sm leading-relaxed">
                Every customer who doesn't see the right cross-sell is a missed
                sale. Over time, that adds up to thousands in lost revenue.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          PRODUCT SPOTLIGHT — bigflow.pairs
          ============================================================ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-500/10 text-brand-500 text-sm font-medium mb-4">
              Available Now
            </div>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="card-gradient rounded-3xl p-8 lg:p-12 border border-[var(--color-border)] hover:border-brand-500/50 transition-all">
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                    bigflow<span className="text-brand-500">.pairs</span>
                  </h2>
                  <p className="text-[var(--color-text-secondary)] text-lg mb-6">
                    Discover what your customers buy together and turn those
                    patterns into product recommendations that sell.
                  </p>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-center gap-3 text-[var(--color-text-secondary)]">
                      <BarChart3 className="h-5 w-5 text-brand-500 flex-shrink-0" />
                      Know exactly which products sell together
                    </li>
                    <li className="flex items-center gap-3 text-[var(--color-text-secondary)]">
                      <Zap className="h-5 w-5 text-brand-500 flex-shrink-0" />
                      Recommendations live on your store in one click
                    </li>
                    <li className="flex items-center gap-3 text-[var(--color-text-secondary)]">
                      <Shield className="h-5 w-5 text-brand-500 flex-shrink-0" />
                      Runs automatically, set it and forget it
                    </li>
                  </ul>
                  <Link
                    to="/pairs"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-xl transition-colors"
                  >
                    Learn More
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>

                {/* Demo Card */}
                <div className="relative">
                  <div className="bg-[var(--color-bg-secondary)] rounded-2xl p-6 border border-[var(--color-border)]">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-[var(--color-text-muted)]">Frequently Bought Together</span>
                      <span className="px-2 py-1 rounded bg-brand-500/10 text-brand-500 text-xs font-bold">
                        HIGH CONFIDENCE
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="p-4 rounded-xl bg-[var(--color-bg)]">
                        <p className="text-xs text-[var(--color-text-muted)] mb-1">Product A</p>
                        <p className="font-medium">Running Shoes</p>
                      </div>
                      <div className="p-4 rounded-xl bg-brand-500/10 border border-brand-500/20">
                        <p className="text-xs text-[var(--color-text-muted)] mb-1">Recommended</p>
                        <p className="text-brand-500 font-medium">Athletic Socks</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm text-[var(--color-text-muted)]">
                      <span>Confidence: <span className="text-[var(--color-text)]">78%</span></span>
                      <span>Lift: <span className="text-[var(--color-text)]">3.2x</span></span>
                    </div>
                    <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
                      <div className="flex items-center gap-2 text-xs text-brand-500">
                        <TrendingUp className="h-3 w-3" />
                        Customers who buy A are 3.2x more likely to buy B
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-[var(--color-text-muted)]">
              More products coming soon.{' '}
              <Link to="/contact" className="text-brand-500 hover:underline">Get notified</Link>
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================
          RESULTS — Concrete impact metrics
          ============================================================ */}
      <section className="py-20 lg:py-28 section-alt">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              The impact of data-driven recommendations
            </h2>
            <p className="text-[var(--color-text-secondary)] text-lg max-w-2xl mx-auto">
              Stop leaving money on the table. Here's what changes when your
              recommendations are powered by real purchase data.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-brand-500/10 flex items-center justify-center">
                <TrendingUp className="h-10 w-10 text-brand-500" />
              </div>
              <div className="text-4xl font-black text-brand-500 mb-2">+AOV</div>
              <h3 className="text-lg font-bold mb-2">Higher Average Order Value</h3>
              <p className="text-[var(--color-text-secondary)] text-sm">
                When customers see relevant cross-sell recommendations, they add
                more items to cart, increasing your revenue per order.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-brand-500/10 flex items-center justify-center">
                <Clock className="h-10 w-10 text-brand-500" />
              </div>
              <div className="text-4xl font-black text-brand-500 mb-2">-Hours</div>
              <h3 className="text-lg font-bold mb-2">Save Hours Every Week</h3>
              <p className="text-[var(--color-text-secondary)] text-sm">
                No more manual analysis or spreadsheet exports. Connect your
                store once and let automation handle the rest.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-brand-500/10 flex items-center justify-center">
                <Target className="h-10 w-10 text-brand-500" />
              </div>
              <div className="text-4xl font-black text-brand-500 mb-2">100%</div>
              <h3 className="text-lg font-bold mb-2">Data-Backed Decisions</h3>
              <p className="text-[var(--color-text-secondary)] text-sm">
                Every recommendation is based on real customer behaviour, not
                assumptions. Sell what the data proves works.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          HOW IT WORKS — 3 steps
          ============================================================ */}
      <section id="how-it-works" className="py-20 lg:py-28 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Three steps. That's it.
            </h2>
            <p className="text-[var(--color-text-secondary)] text-lg max-w-2xl mx-auto">
              From zero to data-driven recommendations in minutes, not days.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center relative">
              <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-[var(--color-border)]" />
              <div className="relative z-10 w-20 h-20 mx-auto mb-6 rounded-2xl bg-brand-500/10 flex items-center justify-center border-2 border-[var(--color-border)]">
                <span className="text-3xl font-black text-brand-500">01</span>
              </div>
              <h3 className="text-xl font-bold mb-3">Connect</h3>
              <p className="text-[var(--color-text-secondary)]">
                Install our Shopify app and connect your store. It takes less
                than a minute.
              </p>
            </div>

            <div className="text-center relative">
              <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-[var(--color-border)]" />
              <div className="relative z-10 w-20 h-20 mx-auto mb-6 rounded-2xl bg-brand-500/10 flex items-center justify-center border-2 border-[var(--color-border)]">
                <span className="text-3xl font-black text-brand-500">02</span>
              </div>
              <h3 className="text-xl font-bold mb-3">Analyze</h3>
              <p className="text-[var(--color-text-secondary)]">
                We import your orders and find the product associations hidden
                in your data.
              </p>
            </div>

            <div className="text-center">
              <div className="relative z-10 w-20 h-20 mx-auto mb-6 rounded-2xl bg-brand-500/10 flex items-center justify-center border-2 border-[var(--color-border)]">
                <span className="text-3xl font-black text-brand-500">03</span>
              </div>
              <h3 className="text-xl font-bold mb-3">Grow</h3>
              <p className="text-[var(--color-text-secondary)]">
                Push recommendations to your store with one click and watch your
                average order value increase.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          TRUST SIGNALS
          ============================================================ */}
      <section className="py-20 lg:py-28 section-alt">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-brand-500/10 flex items-center justify-center">
                <Lock className="h-8 w-8 text-brand-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Your Data Is Safe</h3>
              <p className="text-[var(--color-text-secondary)]">
                Encrypted at rest and in transit. GDPR compliant.
                We never share or sell your data.
              </p>
            </div>
            <div>
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-brand-500/10 flex items-center justify-center">
                <Users className="h-8 w-8 text-brand-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Built for Shopify Merchants</h3>
              <p className="text-[var(--color-text-secondary)]">
                Every feature is designed specifically for e-commerce store
                owners. No enterprise bloat.
              </p>
            </div>
            <div>
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-brand-500/10 flex items-center justify-center">
                <Shield className="h-8 w-8 text-brand-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">No Lock-in, Ever</h3>
              <p className="text-[var(--color-text-secondary)]">
                Cancel anytime. Export your data. 15-day free trial on every
                plan. No questions asked.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          FINAL CTA
          ============================================================ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Every day without data is revenue left on the table
          </h2>
          <p className="text-xl text-[var(--color-text-secondary)] mb-10">
            See what your orders are trying to tell you. It takes less than
            a minute to get started.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/pairs"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-2xl transition-all hover:scale-105"
            >
              Explore bigflow.pairs
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[var(--color-border)] hover:bg-[var(--color-text-muted)]/20 font-semibold rounded-2xl transition-all"
            >
              Talk to Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
